import { motion, useReducedMotion } from "motion/react";
import { type ReactNode, useRef } from "react";
import { createStaggerContainer, createStaggerItem } from "@/src/common/components/animated-section";
import { useEntrance } from "@/src/common/hooks/use-entrance";

type Tone = "ink" | "white";
type Align = "center" | "left";
type HeadingLevel = "h1" | "h2";

interface SectionHeadingProps {
	eyebrow?: ReactNode;
	title: ReactNode;
	titleAccent?: ReactNode;
	description?: ReactNode;
	align?: Align;
	tone?: Tone;
	as?: HeadingLevel;
	animated?: boolean;
	className?: string;
}

export function SectionHeading({
	eyebrow,
	title,
	titleAccent,
	description,
	align = "center",
	tone = "ink",
	as = "h2",
	animated = true,
	className = "",
}: SectionHeadingProps) {
	const ref = useRef<HTMLDivElement>(null);
	const reduced = useReducedMotion() === true;
	const entrance = useEntrance(ref, { disabled: !animated || reduced });
	const isInk = tone === "ink";
	const alignment = align === "center" ? "items-center text-center" : "items-start text-left";
	const Heading = motion[as];
	const container = createStaggerContainer(reduced);
	const item = createStaggerItem(reduced);

	return (
		<motion.div
			ref={ref}
			className={`flex flex-col gap-5 ${alignment} ${className}`}
			initial={false}
			animate={entrance === "hidden" ? "hidden" : "visible"}
			variants={container}
		>
			{eyebrow ? <motion.div variants={item}>{eyebrow}</motion.div> : null}
			<Heading
				variants={item}
				className={`text-balance text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl ${isInk ? "text-foreground" : "text-white"}`}
			>
				{title}
				{titleAccent ? (
					<>
						{" "}
						<span className={isInk ? "text-gradient-brand" : "text-gradient-hero"}>{titleAccent}</span>
					</>
				) : null}
			</Heading>
			{description ? (
				<motion.p variants={item} className={`max-w-2xl text-balance text-base sm:text-lg ${isInk ? "text-default-600" : "text-white/70"}`}>
					{description}
				</motion.p>
			) : null}
		</motion.div>
	);
}
